// Chat Widget Custom Hook
// Manages all state and logic for the chat widget

import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { 
  QUESTIONS, 
  LOCALIZED_MESSAGES, 
  LANGUAGE_STORAGE_KEY, 
  COOKIE_CONSENT_KEY 
} from './chatConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useChatWidget = () => {
  // Core state
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [intakeResult, setIntakeResult] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Consultation scheduling
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedConsultDate, setSelectedConsultDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  
  // TCPA SMS consent
  const [smsConsent, setSmsConsent] = useState(false);
  
  // Cookie consent
  const [showCookieBanner, setShowCookieBanner] = useState(true);
  const [cookieConsented, setCookieConsented] = useState(false);
  
  // Multi-language support
  const [detectedLanguage, setDetectedLanguage] = useState('en');
  const [detectedLanguageName, setDetectedLanguageName] = useState('English');
  const [translatedQuestions, setTranslatedQuestions] = useState(null);
  const [languageDetectionDone, setLanguageDetectionDone] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(true);
  const [loadingTranslations, setLoadingTranslations] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Check cookie consent on mount
  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent === 'accepted') {
      setCookieConsented(true);
      setShowCookieBanner(false);
    }
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset hasInitialized when widget is closed
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  // Helper functions for language preference storage
  const saveLanguagePreference = useCallback((code, name) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify({ code, name }));
    } catch {
      // Silent fail
    }
  }, []);

  const getSavedLanguagePreference = useCallback(() => {
    try {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {
      // Silent fail
    }
    return null;
  }, []);

  // Message helpers
  const addBotMessage = useCallback((text) => {
    setMessages(prev => [...prev, { type: 'bot', text }]);
  }, []);

  const addUserMessage = useCallback((text) => {
    setMessages(prev => [...prev, { type: 'user', text }]);
  }, []);

  // Get the current questions (translated or default)
  const getQuestions = useCallback(() => {
    return translatedQuestions || QUESTIONS;
  }, [translatedQuestions]);

  // Map API questions to widget format
  const mapQuestionsToFormat = useCallback((apiQuestions) => {
    return apiQuestions.map(q => ({
      id: q.id || q.field_name,
      type: q.type === 'select' ? (q.options?.length === 2 && q.options.some(o => o.value === true) ? 'yesno' : 'select') : q.type,
      text: q.question_text,
      field: q.field_name,
      options: q.options,
      conditional: q.conditional,
      optional: !q.required
    }));
  }, []);

  // Handle language selection
  const handleLanguageSelect = useCallback(async (langCode, langName, skipSave = false) => {
    setDetectedLanguage(langCode);
    setDetectedLanguageName(langName);
    setLanguageDetectionDone(true);
    setShowLanguageSelector(false);
    
    if (!skipSave) {
      saveLanguagePreference(langCode, langName);
    }
    
    if (langCode !== 'en') {
      setLoadingTranslations(true);
      try {
        const response = await axios.get(`${API}/intake-questions/translated`, {
          params: { language: langCode }
        });
        
        if (response.data.translated) {
          setTranslatedQuestions(mapQuestionsToFormat(response.data.questions));
          const translatedGreeting = response.data.questions[0]?.question_text || QUESTIONS[0].text;
          addBotMessage(translatedGreeting);
        } else {
          addBotMessage(QUESTIONS[0].text);
        }
      } catch {
        addBotMessage(QUESTIONS[0].text);
      } finally {
        setLoadingTranslations(false);
      }
    } else {
      addBotMessage(QUESTIONS[0].text);
    }
  }, [saveLanguagePreference, mapQuestionsToFormat, addBotMessage]);

  // Check for saved language preference when widget opens
  useEffect(() => {
    if (isOpen && !hasInitialized && messages.length === 0) {
      setHasInitialized(true);
      const savedPref = getSavedLanguagePreference();
      if (savedPref && savedPref.code) {
        handleLanguageSelect(savedPref.code, savedPref.name, true);
      }
    }
  }, [isOpen, hasInitialized, messages.length, getSavedLanguagePreference, handleLanguageSelect]);

  // Handle changing language (reset and show selector again)
  const handleChangeLanguage = useCallback(() => {
    setMessages([]);
    setCurrentStep(0);
    setFormData({});
    setTranslatedQuestions(null);
    setDetectedLanguage('en');
    setDetectedLanguageName('English');
    setShowLanguageSelector(true);
    setLanguageDetectionDone(false);
    setCompleted(false);
    setLoadingTranslations(false);
  }, []);

  // Handle cookie consent
  const handleCookieConsent = useCallback((accepted) => {
    if (accepted) {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
      setCookieConsented(true);
    }
    setShowCookieBanner(false);
  }, []);

  // Detect language from user's text input
  const detectLanguage = useCallback(async (text) => {
    if (languageDetectionDone || text.length < 10) return;
    
    try {
      const response = await axios.post(`${API}/detect-language?text=${encodeURIComponent(text)}`);
      const { code, name } = response.data;
      
      if (code !== 'en') {
        setDetectedLanguage(code);
        setDetectedLanguageName(name);
        
        const translatedResponse = await axios.get(`${API}/intake-questions/translated`, {
          params: { language: code }
        });
        
        if (translatedResponse.data.translated) {
          setTranslatedQuestions(mapQuestionsToFormat(translatedResponse.data.questions));
          addBotMessage(`🌐 I detected you're writing in ${name}. I'll continue in your language.`);
        }
      }
      setLanguageDetectionDone(true);
    } catch {
      setLanguageDetectionDone(true);
    }
  }, [languageDetectionDone, mapQuestionsToFormat, addBotMessage]);

  // Move to next question
  const moveToNextQuestion = useCallback((updatedData = formData) => {
    const questions = getQuestions();
    let nextStep = currentStep + 1;
    
    while (nextStep < questions.length) {
      const nextQ = questions[nextStep];
      if (nextQ.conditional) {
        if (updatedData[nextQ.conditional.field] !== nextQ.conditional.value) {
          nextStep++;
          continue;
        }
      }
      break;
    }

    if (nextStep < questions.length) {
      setCurrentStep(nextStep);
      setTimeout(() => addBotMessage(questions[nextStep].text), 500);
    } else {
      submitIntake(updatedData);
    }
  }, [formData, currentStep, getQuestions, addBotMessage]);

  // Submit intake
  const submitIntake = useCallback(async (data) => {
    setSubmitting(true);
    
    const submissionData = {
      ...data,
      detected_language: detectedLanguage,
      detected_language_name: detectedLanguageName,
      source: 'widget'
    };
    
    addBotMessage(LOCALIZED_MESSAGES.processing[detectedLanguage] || LOCALIZED_MESSAGES.processing.en);

    try {
      const response = await axios.post(`${API}/intakes`, submissionData);
      setIntakeResult(response.data);
      setCompleted(true);
      
      const langMessages = LOCALIZED_MESSAGES.status[detectedLanguage] || LOCALIZED_MESSAGES.status.en;
      addBotMessage(langMessages[response.data.status] || langMessages.review);
      
      if (response.data.status !== 'unlikely') {
        setTimeout(() => {
          addBotMessage(LOCALIZED_MESSAGES.schedule[detectedLanguage] || LOCALIZED_MESSAGES.schedule.en);
          setShowScheduler(true);
        }, 1000);
      }
    } catch {
      addBotMessage(LOCALIZED_MESSAGES.error[detectedLanguage] || LOCALIZED_MESSAGES.error.en);
      setSubmitting(false);
    }
  }, [detectedLanguage, detectedLanguageName, addBotMessage]);

  // Handle answer
  const handleAnswer = useCallback(async (value, displayText) => {
    const questions = getQuestions();
    const question = questions[currentStep];
    addUserMessage(displayText || String(value));
    
    const newFormData = { ...formData, [question.field]: value };
    setFormData(newFormData);
    setInputValue('');
    
    if (typeof value === 'string' && value.length > 10 && !languageDetectionDone) {
      await detectLanguage(value);
    }
    
    moveToNextQuestion(newFormData);
  }, [getQuestions, currentStep, formData, languageDetectionDone, addUserMessage, detectLanguage, moveToNextQuestion]);

  // Handle file upload
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) {
      handleAnswer([], 'No files uploaded');
      return;
    }

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrls.push(response.data.file_url);
      }
      
      handleAnswer(uploadedUrls, `${uploadedUrls.length} file(s) uploaded`);
    } catch {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  }, [handleAnswer]);

  // Fetch available slots for consultation
  const fetchAvailableSlots = useCallback(async (date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/consultations/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data.filter(slot => slot.available));
    } catch {
      // Silent fail
    }
  }, []);

  // Schedule consultation
  const handleScheduleConsultation = useCallback(async () => {
    if (!selectedConsultDate || !selectedSlot) {
      toast.error('Please select a date and time');
      return;
    }

    try {
      await axios.post(`${API}/consultations`, {
        intake_id: intakeResult.id,
        consultation_date: format(selectedConsultDate, 'yyyy-MM-dd'),
        consultation_time: selectedSlot,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone
      });

      setShowScheduler(false);
      addBotMessage(`Your consultation has been scheduled for ${format(selectedConsultDate, 'MMMM d, yyyy')} at ${selectedSlot}. We look forward to speaking with you!`);
      toast.success('Consultation scheduled!');
    } catch {
      toast.error('Failed to schedule consultation');
    }
  }, [selectedConsultDate, selectedSlot, intakeResult, formData, addBotMessage]);

  // Reset chat
  const resetChat = useCallback(() => {
    setMessages([]);
    setCurrentStep(0);
    setFormData({});
    setInputValue('');
    setSelectedDate(null);
    setCompleted(false);
    setIntakeResult(null);
    setShowScheduler(false);
    setSelectedConsultDate(null);
    setAvailableSlots([]);
    setSelectedSlot('');
    setSubmitting(false);
  }, []);

  // Get current question
  const getCurrentQuestion = useCallback(() => {
    const questions = getQuestions();
    return questions[currentStep];
  }, [getQuestions, currentStep]);

  return {
    // State
    isOpen,
    setIsOpen,
    messages,
    currentStep,
    formData,
    inputValue,
    setInputValue,
    selectedDate,
    setSelectedDate,
    uploading,
    submitting,
    completed,
    intakeResult,
    messagesEndRef,
    showScheduler,
    setShowScheduler,
    selectedConsultDate,
    setSelectedConsultDate,
    availableSlots,
    selectedSlot,
    setSelectedSlot,
    smsConsent,
    setSmsConsent,
    showCookieBanner,
    cookieConsented,
    detectedLanguage,
    detectedLanguageName,
    showLanguageSelector,
    loadingTranslations,
    
    // Actions
    handleLanguageSelect,
    handleChangeLanguage,
    handleCookieConsent,
    handleAnswer,
    handleFileUpload,
    handleScheduleConsultation,
    fetchAvailableSlots,
    resetChat,
    getCurrentQuestion,
    getSavedLanguagePreference,
    setFormData,
  };
};

export default useChatWidget;
